import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import cn from "classnames";
import { template } from "lodash";
import { Button, Message, toaster } from "rsuite";

import constants from "utils/constants";

import { getErrorMessage } from "api/defaults";
import { deletePartner } from "api/partners/partners-api";

import ConfirmModal, {
    ConfirmationMode,
} from "components/Common/ConfirmModal/ConfirmModal";

import css from "components/BreadCrumb/BreadCrumb.module.css";

interface ComponentProps {
    partnerName: string;
    partnerId: string;
    setLoaderVisibility: (visible: boolean) => void;
    setIsUnsavedChangeAvailable: (isUnsavedChangeAvailable: boolean) => void;
}

const DeletePartnerButton: React.FC<ComponentProps> = ({
    partnerName,
    partnerId,
    setLoaderVisibility,
    setIsUnsavedChangeAvailable,
}: ComponentProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [confirmVisible, setConfirmVisible] = useState(false);
    const [confirmationDialogMessage, setConfirmationDialogMessage] =
        useState("");

    const handleCancel = () => {
        setConfirmVisible(false);
    };

    const handleConfirm = async () => {
        setLoaderVisibility(true);
        deletePartner(partnerId).then(([payload, status]) => {
            if (status === constants.statusCode.OK) {
                toaster.push(
                    <Message showIcon type="success">
                        <div
                            dangerouslySetInnerHTML={{
                                __html: template(
                                    t("partners.deleteSuccessMessage")
                                )({
                                    partnerName: partnerName,
                                }),
                            }}
                        />
                    </Message>,
                    {
                        duration: constants.alertDurationMsec,
                    }
                );
                navigate(`/partners`);
            } else {
                const errorMessage = getErrorMessage(String(payload), status);
                toaster.push(
                    <Message showIcon type="error">
                        {errorMessage}
                    </Message>
                );
            }
            setLoaderVisibility(false);
        });
        setConfirmVisible(false);
        setIsUnsavedChangeAvailable(false);
    };

    const handleDelete = () => {
        setConfirmVisible(true);
    };

    useEffect(() => {
        if (partnerName) {
            setConfirmationDialogMessage(
                template(t("partners.deleteConfirmationMessage"))({
                    partnerName: partnerName,
                })
            );
        }
    }, [partnerName]);

    return (
        <>
            <ConfirmModal
                onCancel={handleCancel}
                onConfirm={handleConfirm}
                open={confirmVisible}
                message={confirmationDialogMessage}
                mode={ConfirmationMode.Destructive}
            />

            <Button
                onClick={handleDelete}
                appearance="primary"
                className={cn(css.addNewCustomerBtn, css.deleteBtn)}
            >
                {t("partners.deleteButton")}
            </Button>
        </>
    );
};

export default DeletePartnerButton;
